'use client';

import * as React from 'react';
import styles from './Radio.module.css';
import clsx from 'clsx';

export interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  orientation?: 'horizontal' | 'vertical';
  children: React.ReactNode;
  className?: string;
}

export interface RadioProps {
  value: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

interface RadioContextValue {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioContext = React.createContext<RadioContextValue | null>(null);

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled = false,
      name,
      orientation = 'vertical',
      children,
      className,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const currentValue = value ?? internalValue;
    const generatedId = React.useId();
    const groupName = name || generatedId;

    const handleChange = (newValue: string) => {
      setInternalValue(newValue);
      onChange?.(newValue);
    };

    return (
      <RadioContext.Provider
        value={{
          name: groupName,
          value: currentValue,
          onChange: handleChange,
          disabled,
        }}
      >
        <div
          ref={ref}
          role="radiogroup"
          className={clsx(styles.group, styles[orientation], className)}
        >
          {children}
        </div>
      </RadioContext.Provider>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ value, label, description, disabled = false, className }, ref) => {
    const context = React.useContext(RadioContext);
    const isDisabled = disabled || context?.disabled;
    const isChecked = context?.value === value;

    const handleChange = () => {
      if (!isDisabled) {
        context?.onChange?.(value);
      }
    };

    return (
      <label className={clsx(styles.label, isDisabled && styles.disabled, className)}>
        <span className={styles.radioWrapper}>
          <input
            ref={ref}
            type="radio"
            name={context?.name}
            value={value}
            checked={isChecked}
            disabled={isDisabled}
            onChange={handleChange}
            className={styles.input}
          />
          <span className={clsx(styles.radio, isChecked && styles.checked)}>
            <span className={styles.indicator} />
          </span>
        </span>
        {label && (
          <span className={styles.labelText}>
            {label}
            {description && <span className={styles.description}>{description}</span>}
          </span>
        )}
      </label>
    );
  }
);

Radio.displayName = 'Radio';
